import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { ProductImg } from "./ProductImg";

export const Lightbox = ({ images, open, initialIndex = 0, onClose, alt }) => {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const prev = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length],
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % images.length),
    [images.length],
  );

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, prev, next]);

  // Simple swipe support
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (delta > 60) prev();
    else if (delta < -60) next();
    setTouchStart(null);
  };

  if (!open) return null;

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        onClick={onClose}
        data-testid="lightbox-close"
        className="absolute top-5 right-5 p-3 border border-white/20 hover:border-yellow-400 hover:text-yellow-400 transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            data-testid="lightbox-prev"
            className="absolute left-4 md:left-8 p-3 border border-white/20 hover:border-yellow-400 hover:text-yellow-400 transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            data-testid="lightbox-next"
            className="absolute right-4 md:right-8 p-3 border border-white/20 hover:border-yellow-400 hover:text-yellow-400 transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      <div className="w-full h-full flex items-center justify-center p-4 md:p-16">
        <div className="max-w-full max-h-full">
          <ProductImg
            src={images[index]}
            alt={alt}
            className="max-w-full max-h-[80vh] object-contain select-none"
            draggable={false}
          />
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            className={`w-6 h-1 transition-colors ${
              index === i ? "bg-yellow-400" : "bg-white/30"
            }`}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
